/**
 * The operator→robot command channel: a single SCTP DataChannel ("chat" in the wire
 * protocol) that carries every teleop-commands message. Owns its own send queue — a
 * command sent before the channel is open (or while it's being rebuilt during a
 * rejoin) is queued here rather than dropped, and flushed once the channel reports
 * `open`. This is everything any other file needs to know about the chat channel; how
 * the underlying DataProducer gets created is `connect-phase.ts`'s job, not this file's
 * — it only ever hands an already-created producer to `attach()`.
 */

import type { types as Mediasoup } from "mediasoup-client";

import { emitStatus, type EmitStatusOpts } from "./session-status";
import { STATUS_CODES } from "../status-codes";

export interface CreateChatChannelDeps {
  appendOperatorDcMessage: (text: string) => void;
  appendDiag: (s: string, opts?: { level?: string }) => void;
  opts: EmitStatusOpts;
  queueWhenClosed?: boolean;
}

export interface ChatChannel {
  attach: (producer: Mediasoup.DataProducer, hooks?: { onOpen?: () => void }) => void;
  send: (text: string) => boolean;
  close: () => void;
}

export function createChatChannel({
  appendOperatorDcMessage,
  appendDiag,
  opts,
  queueWhenClosed = true,
}: CreateChatChannelDeps): ChatChannel {
  let dataProducer: Mediasoup.DataProducer | null = null;
  const pending: string[] = [];

  function drain() {
    while (pending.length) {
      const message = pending.shift();
      if (message === undefined) break;
      try {
        dataProducer?.send(message);
      } catch (err) {
        appendDiag(`[chat] drain send failed: ${err}`, { level: "warn" });
        pending.unshift(message);
        break;
      }
    }
  }

  /**
   * Wire up a freshly produced DataProducer — replaces any previous one wholesale (a
   * rejoin always creates a new producer; the old one is closed via `close()` first by
   * `teardown-phase.ts` before a new one is ever attached).
   */
  function attach(producer: Mediasoup.DataProducer, hooks: { onOpen?: () => void } = {}) {
    dataProducer = producer;
    producer.on("open", () => {
      drain();
      hooks.onOpen?.();
    });
  }

  /** @returns true if sent immediately, false if queued */
  function send(text: string): boolean {
    appendOperatorDcMessage(text);
    if (dataProducer?.readyState === "open") {
      try {
        dataProducer.send(text);
        return true;
      } catch (err) {
        // TOCTOU: the channel can close between the readyState check above and send()
        // (network drop, peer teardown). Fall through to queueing instead of throwing.
        appendDiag(`[chat] send failed: ${err}`, { level: "warn" });
        emitStatus(opts, STATUS_CODES.COMMAND_CHANNEL_UNAVAILABLE);
      }
    }
    if (queueWhenClosed) pending.push(text);
    return false;
  }

  function close() {
    try {
      if (dataProducer && !dataProducer.closed) {
        dataProducer.close();
      }
    } catch {
      /* ignore */
    }
    dataProducer = null;
    // A rejoin tears down and recreates this channel from scratch — anything still
    // queued (e.g. stale motion.velocity ticks from before the disconnect) must not
    // survive into the freshly attached channel, or it replays in one burst on open.
    pending.length = 0;
  }

  return {
    attach,
    send,
    close,
  };
}
